import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { fileUrl, formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { WeightChart } from "@/pages/client/ClientHome";
import { Send, Mic, Square, Video, Eye, ArrowLeft } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const [client, setClient] = useState(null);
  const HEALTH_RED_FIELDS = ["chest_pain", "fainting", "cardio_limit", "medical_avoid"];
  const [aiBusy, setAiBusy] = useState(false);
  const [aiWeeks, setAiWeeks] = useState(8);
  const [aiDays, setAiDays] = useState(3);
  const navigate = useNavigate();

  const generateAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/coach/programs/generate-ai", {
        client_id: id, weeks: Number(aiWeeks) || 8, days_per_week: Number(aiDays) || 3,
      });
      sessionStorage.setItem("ai_draft_program", JSON.stringify(data.program));
      if (data.warnings?.length) toast.warning(`Esercizi non mappati: ${data.warnings.join(", ")}`);
      navigate("/coach/programs/new");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  const resetHealth = async () => {
    if (!window.confirm(t("detail.resetHealthConfirm"))) return;
    try {
      await api.post(`/coach/clients/${id}/reset-onboarding`);
      toast.success(t("detail.resetHealthDone"));
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const HEALTH_LABELS = [
    ["birth_date", "Data di nascita"], ["sex", "Sesso"], ["work_activity", "Attività lavorativa"], ["daily_steps", "Passi/giorno"],
    ["objectives", "Obiettivi"], ["results_text", "Risultati attesi"], ["motivation", "Motivazione (1-5)"], ["timeframe", "Aspettative"],
    ["trained_before", "Esperienza"], ["trains_now", "Si allena ora"], ["current_activities", "Attività attuali"], ["training_places", "Dove si allena"],
    ["has_condition", "Condizione medica attuale"], ["condition_details", "Dettagli condizione"],
    ["chest_pain", "Dolore al petto"], ["fainting", "Svenimenti/capogiri"], ["cardio_limit", "Problema cardiovascolare"], ["medical_avoid", "Divieto medico esercizio"],
    ["injury_current", "Infortunio/dolore attuale"], ["pain_areas", "Aree dolore"], ["pain_intensity", "Intensità (0-10)"], ["pain_duration", "Da quanto"],
    ["pain_worse", "Peggiora con"], ["pain_diagnosis", "Diagnosi medico/fisio"], ["pain_indications", "Indicazioni da rispettare"],
    ["diagnosed", "Patologie diagnosticate"], ["diagnosed_areas", "Aree patologie"], ["surgeries", "Interventi chirurgici"], ["surgeries_details", "Dettagli interventi"],
    ["medications_flag", "Farmaci/trattamenti"], ["med_name", "Farmaco"], ["med_since", "Da quanto"], ["med_indications", "Indicazioni medico"],
    ["female_status", "Profilo femminile"], ["cycle_regular", "Ciclo regolare"], ["female_symptoms", "Sintomi rilevanti"], ["female_notes", "Note profilo femminile"],
    ["sleep_hours", "Ore di sonno"], ["sleep_quality", "Qualità sonno (1-5)"], ["stress", "Stress (1-5)"], ["energy", "Energia (1-5)"], ["realistic_days", "Giorni allenamento/sett"],
    ["session_length", "Durata sessione"], ["equipment_list", "Attrezzatura"],
    ["diet_description", "Alimentazione"], ["food_allergies", "Allergie/intolleranze"], ["food_allergies_details", "Dettagli allergie"],
    ["diet_regime", "Regime alimentare"], ["foods_excluded", "Alimenti esclusi"],
    ["alcohol", "Alcol"], ["smoking", "Fumo"], ["meals_per_day", "Pasti/giorno"], ["water", "Acqua"],
  ];
  const [programs, setPrograms] = useState([]);
  const [stretchRoutines, setStretchRoutines] = useState([]);
  const [logs, setLogs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [messages, setMessages] = useState([]);
  const [nutriPlans, setNutriPlans] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [tempPw, setTempPw] = useState("");
  const [giftMonths, setGiftMonths] = useState(3);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const loadMessages = () => api.get(`/coach/clients/${id}/messages`).then((r) => setMessages(r.data)).catch(() => {});

  useEffect(() => {
    const iv = setInterval(loadMessages, 5000);
    return () => clearInterval(iv);
  }, [id]);

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    await api.post(`/coach/clients/${id}/messages`, { text: msgText });
    setMsgText("");
    loadMessages();
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const ext = (rec.mimeType || "").includes("mp4") ? "m4a" : "webm";
        const fd = new FormData();
        fd.append("file", blob, `nota.${ext}`);
        try {
          await api.post(`/coach/clients/${id}/messages/audio`, fd);
          loadMessages();
        } catch (e) {
          toast.error(formatApiError(e));
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error(t("chat.micDenied"));
    }
  };

  const stopRec = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const resetPw = async () => {
    const { data } = await api.post(`/coach/clients/${id}/reset-password`);
    setTempPw(data.temp_password);
  };

  const toggleDisabled = async () => {
    const { data } = await api.post(`/coach/clients/${id}/toggle-disabled`);
    toast.success(data.disabled ? t("coach.disabled") : "OK");
    load();
  };

  const giftAccess = async () => {
    const now = new Date();
    const current = client.package_expires_at ? new Date(client.package_expires_at) : null;
    const base = current && current > now ? current : now;
    const d = new Date(base);
    d.setMonth(d.getMonth() + giftMonths);
    await patch({ package_expires_at: d.toISOString().slice(0, 10) });
    toast.success(t("detail.giftDone"));
  };

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard") },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  const [phys, setPhys] = useState({ weight_kg: "", height_cm: "", body_fat_pct: "" });

  const load = () => {
    api.get("/coach/clients").then((r) => {
      const c = r.data.find((x) => x._id === id) || null;
      setClient(c);
      if (c) setPhys({ weight_kg: c.weight_kg ?? "", height_cm: c.height_cm ?? "", body_fat_pct: c.body_fat_pct ?? "" });
    });
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/coach/stretch-routines").then((r) => setStretchRoutines(r.data)).catch(() => {});
    api.get(`/coach/clients/${id}/logs`).then((r) => setLogs(r.data)).catch(() => {});
    api.get(`/coach/clients/${id}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
    api.get(`/coach/clients/${id}/feedback`).then((r) => setFeedback(r.data)).catch(() => {});
    api.get(`/coach/clients/${id}/body-analyses`).then((r) => setAnalyses(r.data)).catch(() => {});
    api.get(`/coach/clients/${id}/measurements`).then((r) => setMeasures(r.data)).catch(() => {});
    api.get("/nutrition-plans").then((r) => setNutriPlans(r.data)).catch(() => {});
    loadMessages();
  };
  useEffect(load, [id]);

  const patch = async (payload) => {
    try {
      const { data } = await api.patch(`/coach/clients/${id}`, payload);
      setClient(data);
      toast.success(t("common.save") + " ✓");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const savePhys = async () => {
    await patch({
      weight_kg: phys.weight_kg !== "" ? Number(phys.weight_kg) : null,
      height_cm: phys.height_cm !== "" ? Number(phys.height_cm) : null,
      body_fat_pct: phys.body_fat_pct !== "" ? Number(phys.body_fat_pct) : null,
    });
  };

  if (!client) return <Layout nav={nav}><p className="text-muted-foreground">{t("common.loading")}</p></Layout>;

  const program = programs.find((p) => p._id === client.assigned_program_id);
  const nutriPlan = nutriPlans.find((p) => p.client_id === id);
  const phases = program?.phases || [];
  const weeks = phases[client.current_phase_idx]?.months?.flatMap((m) => m.weeks || []) || [];

  return (
    <Layout nav={nav}>
      <div data-testid="client-detail-page" className="space-y-8">
        <div>
          <Link to="/coach/clients" data-testid="client-detail-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="w-4 h-4" /> {t("nav.clients")}
          </Link>
          <p className="kc-eyebrow">{t("coach.clientDetail")}</p>
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">{client.name}</h1>
          <p className="text-muted-foreground">{client.email}</p>
          <span className={`kc-badge mt-2 ${client.blocked ? "bg-destructive/15 text-destructive" : "bg-green-500/15 text-green-500"}`}
            data-testid="client-detail-status">
            {client.blocked ? t("coach.status.expired") : t("coach.status.active")}
          </span>
          {client.disabled && <span className="kc-badge bg-destructive/15 text-destructive ml-2" data-testid="client-disabled-badge">{t("coach.disabled")}</span>}
          <div className="flex gap-2 mt-4 flex-wrap">
            <button data-testid="reset-client-password-button" onClick={resetPw} className="kc-btn-secondary !py-2 text-sm">{t("coach.resetPw")}</button>
            <button data-testid="toggle-disabled-button" onClick={toggleDisabled} className="kc-btn-secondary !py-2 text-sm">
              {client.disabled ? t("coach.enable") : t("coach.disable")}
            </button>
          </div>
          {tempPw && (
            <p className="mt-3 text-sm font-mono bg-secondary/60 rounded-lg p-3" data-testid="temp-pw-box">
              {t("coach.newPw")} <span className="text-accent font-bold">{tempPw}</span>
            </p>
          )}
        </div>

        <div className="kc-card p-5" data-testid="client-data-card">
          <p className="kc-eyebrow mb-3">{t("client.data")}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            {[["weight_kg", "field.weight", "kg", "client-weight"], ["height_cm", "field.height", "cm", "client-height"], ["body_fat_pct", "ms.bf", "%", "client-bodyfat"]].map(([k, label, unit, testid]) => (
              <div key={k}>
                <p className="text-muted-foreground text-xs uppercase">{t(label)}</p>
                <div className="flex items-center gap-1 mt-1">
                  <input data-testid={`${testid}-input`} type="number" min="0" step="0.1" value={phys[k]}
                    onChange={(e) => setPhys({ ...phys, [k]: e.target.value })} className="kc-input !py-1.5 font-mono" />
                  <span className="text-xs text-muted-foreground shrink-0">{unit}</span>
                </div>
              </div>
            ))}
            <div className="flex items-end">
              <button data-testid="save-phys-button" onClick={savePhys} className="kc-btn-secondary !py-1.5 !px-4 text-xs">{t("common.save")}</button>
            </div>
            <div><p className="text-muted-foreground text-xs uppercase">{t("field.allergies")}</p><p data-testid="client-allergies">{client.allergies || "—"}</p></div>
            <div><p className="text-muted-foreground text-xs uppercase">{t("field.injuries")}</p><p data-testid="client-injuries">{client.injuries || "—"}</p></div>
            <div><p className="text-muted-foreground text-xs uppercase">{t("field.medications")}</p><p data-testid="client-medications">{client.medications || "—"}</p></div>
            <div><p className="text-muted-foreground text-xs uppercase">{t("q.goals")}</p><p data-testid="client-goals">{client.goals || "—"}</p></div>
            <div><p className="text-muted-foreground text-xs uppercase">{t("q.experience")}</p><p data-testid="client-experience">{client.experience || "—"}</p></div>
            <div><p className="text-muted-foreground text-xs uppercase">{t("q.availability")}</p><p data-testid="client-availability">{client.availability ?? "—"}</p></div>
            <div><p className="text-muted-foreground text-xs uppercase">{t("q.equipment")}</p><p data-testid="client-equipment">{client.equipment || "—"}</p></div>
          </div>
        </div>

        {client.health_check && Object.keys(client.health_check).length > 0 && (
          <div className="kc-card p-5" data-testid="client-health-card">
            <p className="kc-eyebrow mb-3 flex items-center gap-2">
              Health Check
              <span data-testid="client-health-status" className={`kc-badge inline-flex items-center gap-1.5 ${client.health_status === "red" ? "bg-destructive/15 text-destructive" : client.health_status === "yellow" ? "bg-amber-500/15 text-amber-500" : "bg-green-500/15 text-green-500"}`}>
                <span className={`w-2 h-2 rounded-full ${client.health_status === "red" ? "bg-destructive" : client.health_status === "yellow" ? "bg-amber-500" : "bg-green-500"}`} />
                {t(`hc.status.${client.health_status || "green"}`)}
              </span>
              <button data-testid="health-reset-button" onClick={resetHealth}
                className="ml-auto text-xs font-bold text-muted-foreground hover:text-destructive transition-colors">
                {t("detail.resetHealth")}
              </button>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
              {HEALTH_LABELS.filter(([k]) => {
                const v = client.health_check[k];
                return v !== undefined && v !== "" && (!Array.isArray(v) || v.length > 0);
              }).map(([k, label]) => {
                const v = client.health_check[k];
                const text = Array.isArray(v) ? v.join(", ") : v === "yes" ? "Sì" : v === "no" ? "No" : String(v);
                const flag = HEALTH_RED_FIELDS.includes(k) && v === "yes";
                return (
                  <div key={k} data-testid={`hc-row-${k}`} className="flex justify-between gap-3 border-b border-border/50 pb-1.5">
                    <span className="text-muted-foreground text-xs uppercase tracking-wide shrink-0">{label}</span>
                    <span className={`text-right ${flag ? "text-destructive font-bold" : ""}`}>{text}{flag ? " ⚠" : ""}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="kc-card p-5" data-testid="client-nutrition-card">
          <p className="kc-eyebrow mb-3">{t("nav.nutrition")}</p>
          {nutriPlan ? (
            <div>
              <Link to={`/coach/nutrition?edit=${nutriPlan._id}`} data-testid="client-nutrition-plan-link"
                className="font-bold text-primary hover:underline inline-flex items-center gap-1.5">
                {nutriPlan.name} <Eye className="w-4 h-4" />
              </Link>
              <div className="flex gap-2 mt-2 flex-wrap font-mono text-xs">
                {nutriPlan.kcal_target != null && <span className="kc-badge bg-primary/15 text-primary">{nutriPlan.kcal_target} kcal</span>}
                {nutriPlan.protein_g != null && <span className="kc-badge bg-secondary text-muted-foreground">P {nutriPlan.protein_g}g</span>}
                {nutriPlan.carbs_g != null && <span className="kc-badge bg-secondary text-muted-foreground">C {nutriPlan.carbs_g}g</span>}
                {nutriPlan.fat_g != null && <span className="kc-badge bg-secondary text-muted-foreground">F {nutriPlan.fat_g}g</span>}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="client-nutrition-empty">{t("nu.noPlanAssigned")}</p>
          )}
        </div>

        <div className="kc-card p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="client-assignment-panel">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("coach.assignProgram")}</label>
            <select data-testid="detail-program-select" value={client.assigned_program_id || ""}
              onChange={(e) => patch({ assigned_program_id: e.target.value || null })} className="kc-input mt-1">
              <option value="">{t("coach.none")}</option>
              {programs.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            {client.assigned_program_id && (
              <Link to={`/coach/programs?view=${client.assigned_program_id}`} data-testid="view-assigned-program-link"
                className="text-xs font-bold text-primary hover:underline mt-1.5 inline-flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" /> {t("pr.preview")}
              </Link>
            )}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("st.assign")}</label>
            <select data-testid="detail-stretch-select" value={client.assigned_stretch_routine_id || ""}
              onChange={(e) => patch({ assigned_stretch_routine_id: e.target.value || null })} className="kc-input mt-1">
              <option value="">{t("coach.none")}</option>
              {stretchRoutines.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("coach.phase")}</label>
            <select data-testid="detail-phase-select" value={client.current_phase_idx ?? 0}
              onChange={(e) => patch({ current_phase_idx: Number(e.target.value), current_week_idx: 0 })} className="kc-input mt-1">
              {phases.map((p, i) => <option key={p.id} value={i}>{p.name}</option>)}
              {phases.length === 0 && <option value={0}>—</option>}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("coach.week")}</label>
            <select data-testid="detail-week-select" value={client.current_week_idx ?? 0}
              onChange={(e) => patch({ current_week_idx: Number(e.target.value) })} className="kc-input mt-1">
              {weeks.map((w, i) => <option key={w.id || i} value={i}>{w.name}</option>)}
              {weeks.length === 0 && <option value={0}>—</option>}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("coach.expiry")}</label>
            <input data-testid="detail-expiry-input" type="date"
              value={client.package_expires_at ? client.package_expires_at.slice(0, 10) : ""}
              onChange={(e) => patch({ package_expires_at: e.target.value || null })} className="kc-input mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("detail.giftTitle")}</label>
            <div className="flex gap-1.5 mt-1">
              <select data-testid="gift-months-select" value={giftMonths} onChange={(e) => setGiftMonths(Number(e.target.value))} className="kc-input">
                {[1, 2, 3, 4, 6, 9, 12].map((m) => <option key={m} value={m}>{m} {t("detail.giftMonths")}</option>)}
              </select>
              <button data-testid="gift-activate-button" onClick={giftAccess} className="kc-btn-secondary !py-2 text-xs whitespace-nowrap">
                {t("detail.giftButton")}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("detail.aiGenerate")}</label>
            <div className="flex gap-1.5 mt-1">
              <input type="number" min="2" max="16" value={aiWeeks} onChange={(e) => setAiWeeks(e.target.value)}
                className="kc-input !w-14 !px-2 text-center" data-testid="ai-weeks-input" title={t("detail.aiWeeks")} />
              <input type="number" min="1" max="6" value={aiDays} onChange={(e) => setAiDays(e.target.value)}
                className="kc-input !w-14 !px-2 text-center" data-testid="ai-days-input" title={t("detail.aiDays")} />
              <button data-testid="ai-generate-button" onClick={generateAi} disabled={aiBusy || !client.onboarding_completed}
                className="kc-btn-secondary !py-2 text-xs whitespace-nowrap">
                {aiBusy ? t("detail.aiGenerating") : t("detail.aiGenerate")}
              </button>
            </div>
          </div>
        </div>

        <section>
          <h2 className="text-xl font-bold mb-3">{t("chat.title")}</h2>
          <div className="kc-card p-4 space-y-3" data-testid="coach-chat">
            <div className="max-h-72 overflow-y-auto space-y-2" data-testid="coach-chat-thread">
              {messages.length === 0 && <p className="text-sm text-muted-foreground">{t("chat.empty")}</p>}
              {messages.map((m) => (
                <div key={m._id} data-testid="coach-chat-message"
                  className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.sender === "coach" ? "bg-primary text-primary-foreground ml-auto" : "bg-secondary"}`}>
                  {m.audio_url ? (
                    <audio controls preload="none" src={m.audio_url} className="w-52 max-w-full" data-testid="coach-chat-audio-player" />
                  ) : (
                    <p className="whitespace-pre-wrap">{m.text}</p>
                  )}
                  <p className="text-[10px] opacity-70 font-mono mt-0.5">{m.created_at?.slice(5, 16).replace("T", " ")}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <input data-testid="coach-chat-input" value={msgText} onChange={(e) => setMsgText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder={t("chat.placeholder")} className="kc-input flex-1" />
              <button type="button" data-testid="coach-chat-mic" onClick={recording ? stopRec : startRec}
                className={`w-10 h-10 rounded-full grid place-items-center border-2 shrink-0 transition-colors ${
                  recording ? "bg-destructive border-destructive text-white animate-pulse" : "border-border text-muted-foreground hover:border-primary/60"
                }`}>
                {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <button data-testid="coach-chat-send" onClick={sendMsg} className="kc-btn-primary !px-4 !py-2"><Send className="w-4 h-4" /></button>
              <a href="https://meet.google.com/new" target="_blank" rel="noopener noreferrer" data-testid="coach-videocall-button"
                className="kc-btn-ghost !px-3 !py-2 inline-flex items-center gap-1.5 text-xs font-bold text-primary shrink-0">
                <Video className="w-4 h-4" /> Meet
              </a>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-3">{t("ms.title")} ({measures.length})</h2>
          <WeightChart entries={measures} />
          {measures.length === 0 ? <p className="text-muted-foreground text-sm">—</p> : (
            <div className="space-y-2" data-testid="client-measurements-list">
              {measures.map((m) => (
                <div key={m._id} className="kc-card p-4 flex flex-wrap items-center gap-3 text-sm" data-testid="measurement-card">
                  <span className="font-mono text-muted-foreground">{m.date}</span>
                  <span className="font-mono">
                    {[
                      m.weight_kg != null && `${m.weight_kg}kg`,
                      m.waist_cm != null && `V ${m.waist_cm}`,
                      m.hips_cm != null && `F ${m.hips_cm}`,
                      m.chest_cm != null && `P ${m.chest_cm}`,
                      m.arm_cm != null && `B ${m.arm_cm}`,
                      m.thigh_cm != null && `C ${m.thigh_cm}`,
                    ].filter(Boolean).join(" · ") || "—"}
                  </span>
                  {m.notes && <span className="text-muted-foreground italic w-full">"{m.notes}"</span>}
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-xl font-bold mb-3">{t("bf.title")} ({analyses.length})</h2>
          {analyses.length === 0 ? <p className="text-muted-foreground text-sm">—</p> : (
            <div className="space-y-2" data-testid="client-analyses-list">
              {analyses.map((a) => (
                <div key={a._id} className="kc-card p-4 flex flex-wrap items-center gap-3" data-testid="analysis-card">
                  <span className="font-mono text-2xl font-black text-primary">{a.bf_min}–{a.bf_max}%</span>
                  <span className="kc-badge bg-secondary text-muted-foreground">{t("bf.confidence")}: {a.confidence}</span>
                  <span className="text-sm text-muted-foreground font-mono ml-auto">{a.created_at?.slice(0, 10)}</span>
                  {a.observations && <p className="text-sm text-muted-foreground w-full">{a.observations}</p>}
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-xl font-bold mb-3">{t("fb.title")} ({feedback.length})</h2>
          {feedback.length === 0 ? <p className="text-muted-foreground text-sm">—</p> : (
            <div className="space-y-3" data-testid="client-feedback-list">
              {feedback.map((f) => (
                <div key={f._id} className="kc-card p-4" data-testid="feedback-card">
                  <div className="flex justify-between mb-3">
                    <span className="font-bold">{f.phase_name || `${t("coach.phase")} ${f.phase_idx + 1}`}</span>
                    <span className="text-sm text-muted-foreground font-mono">{f.submitted_at?.slice(0, 10)}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    {["q1", "q2", "q3", "q4", "q5", "q6"].map((k) => (
                      <div key={k}>
                        <p className="text-muted-foreground text-xs uppercase tracking-wider">{t(`fb.${k}`)}</p>
                        <p className={k === "q2" ? "font-mono font-bold text-primary" : ""}>
                          {k === "q2" ? `${f.answers?.[k]}/10` : f.answers?.[k]}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-xl font-bold mb-3">{t("coach.logs")} ({logs.length})</h2>
          {logs.length === 0 ? <p className="text-muted-foreground text-sm">{t("hist.empty")}</p> : (
            <div className="space-y-3" data-testid="client-logs-list">
              {logs.map((log) => (
                <div key={log._id} className="kc-card p-4">
                  <div className="flex justify-between mb-2">
                    <span className="font-bold">{log.routine_name || "—"}</span>
                    <span className="text-sm text-muted-foreground font-mono">{log.date?.slice(0, 10)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {log.entries?.map((e, i) => (
                      <p key={i}>{e.exercise_name}: {e.sets?.filter((s) => s.done).map((s) => `${s.weight}kg×${s.reps}`).join(", ") || "—"}</p>
                    ))}
                    {log.notes && <p className="italic">"{log.notes}"</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-xl font-bold mb-3">{t("coach.photos")} ({photos.length})</h2>
          {photos.length === 0 ? <p className="text-muted-foreground text-sm">{t("ph.empty")}</p> : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="client-photos-grid">
              {photos.map((p) => (
                <figure key={p._id} className="kc-card overflow-hidden">
                  <img src={fileUrl(p.storage_path)} alt={p.kind} className="w-full h-48 object-cover" loading="lazy" />
                  <figcaption className="p-2 text-xs flex justify-between">
                    <span className={`font-bold uppercase ${p.kind === "before" ? "text-amber-500" : "text-green-500"}`}>
                      {p.kind === "before" ? t("ph.before") : t("ph.after")}
                    </span>
                    <span className="text-muted-foreground font-mono">{p.created_at?.slice(0, 10)}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
